import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Store, Check, X, Building2, UserPlus, Loader2, Mail, Phone, MapPin, FileText, Calendar, User } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = { pending: "In afwachting", approved: "Goedgekeurd", rejected: "Afgewezen" };
const STATUS_KLEUR = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
};

export default function WebshopAanvragen() {
  const [tab, setTab] = useState("pending");
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actief, setActief] = useState(null); // account in goedkeur-dialoog
  const [orgQ, setOrgQ] = useState("");
  const [orgResults, setOrgResults] = useState([]);
  const [gekozenOrg, setGekozenOrg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [duplicaten, setDuplicaten] = useState([]);
  const [dupLoading, setDupLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/shop/accounts", { params: { status: tab } });
      setAccounts(data || []);
    } catch { toast.error("Laden mislukt"); }
    finally { setLoading(false); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const zoekOrg = async (v) => {
    setOrgQ(v); setGekozenOrg(null);
    if (!v.trim()) { setOrgResults([]); return; }
    try { const { data } = await api.get("/organisaties", { params: { q: v } });
      setOrgResults((Array.isArray(data) ? data : data.items || []).slice(0, 6)); } catch (e) { console.warn("Organisatie zoeken mislukt:", e); }
  };

  const openGoedkeuren = async (acc) => {
    setActief(acc); setOrgQ(""); setOrgResults([]); setGekozenOrg(null); setDuplicaten([]);
    setDupLoading(true);
    try {
      const { data } = await api.get(`/shop/accounts/${acc.id}/mogelijke-duplicaten`);
      setDuplicaten(data?.duplicaten || []);
    } catch { setDuplicaten([]); }
    finally { setDupLoading(false); }
  };

  const bevestigGoedkeuren = async () => {
    setBusy(true);
    try {
      await api.post(`/shop/accounts/${actief.id}/approve`,
        { organisatie_id: gekozenOrg?.id || null });
      toast.success(gekozenOrg ? "Goedgekeurd en gekoppeld" : "Goedgekeurd — nieuwe organisatie aangemaakt");
      setActief(null); load();
      window.dispatchEvent(new Event("webshop-updated"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Goedkeuren mislukt"); }
    finally { setBusy(false); }
  };

  const afwijzen = async (acc) => {
    if (!window.confirm(`Aanvraag van ${acc.bedrijfsnaam} afwijzen?`)) return;
    await api.post(`/shop/accounts/${acc.id}/reject`, {});
    toast.success("Afgewezen"); load();
    window.dispatchEvent(new Event("webshop-updated"));
  };

  return (
    <div className="space-y-6" data-testid="webshop-aanvragen-page">
      <div>
        <div className="text-sm text-muted-foreground flex items-center gap-1.5"><Store className="h-3.5 w-3.5 text-accent" /> Webshop</div>
        <h1 className="font-display text-4xl font-medium tracking-tight mt-1">Webshop-aanvragen</h1>
        <p className="text-sm text-muted-foreground mt-1">Beoordeel nieuwe accountaanvragen van de B2B-webshop. Goedgekeurde klanten kunnen inloggen en bestellen.</p>
      </div>

      <div className="flex gap-1.5">
        {["pending", "approved", "rejected"].map((s) => (
          <button key={s} onClick={() => setTab(s)} data-testid={`tab-${s}`}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${tab === s ? "bg-foreground text-background border-foreground" : "bg-card border-border hover:bg-secondary"}`}>
            {STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
        ) : accounts.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground text-sm">Geen aanvragen in deze categorie.</div>
        ) : (
          <Accordion type="multiple" className="divide-y divide-border" data-testid="aanvragen-accordion">
            {accounts.map((a) => (
              <AccordionItem key={a.id} value={a.id} data-testid={`aanvraag-${a.id}`} className="border-0">
                <div className="flex items-center gap-2 px-4">
                  <AccordionTrigger className="flex-1 hover:no-underline py-3 min-w-0">
                    <div className="flex items-center gap-3 flex-1 min-w-0 text-left">
                      <div className="p-1.5 rounded-md bg-secondary shrink-0">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{a.bedrijfsnaam}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {a.voornaam} {a.achternaam} · {a.email}
                        </div>
                      </div>
                      <Badge variant="outline" className={`shrink-0 ${STATUS_KLEUR[a.status]}`}>{STATUS_LABEL[a.status]}</Badge>
                    </div>
                  </AccordionTrigger>
                  {a.status === "pending" && (
                    <div className="flex items-center gap-2 py-2 shrink-0">
                      <Button size="sm" onClick={() => openGoedkeuren(a)} data-testid={`goedkeur-${a.id}`}>
                        <Check className="h-4 w-4 mr-1" /> Goedkeuren
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => afwijzen(a)} data-testid={`afwijs-${a.id}`}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  {a.status === "approved" && a.organisatie_id && (
                    <a href={`/organisaties/${a.organisatie_id}`} className="text-accent hover:underline text-xs inline-flex items-center gap-1 py-2 shrink-0">
                      <Building2 className="h-3.5 w-3.5" /> Naar organisatie
                    </a>
                  )}
                </div>
                <AccordionContent className="px-4 pb-4">
                  <div className="grid sm:grid-cols-2 gap-4 pt-1 text-sm" data-testid={`aanvraag-details-${a.id}`}>
                    {/* Contactgegevens */}
                    <div className="border border-border rounded-lg p-3 bg-secondary/20 space-y-2">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">Contactpersoon</div>
                      <div className="flex items-start gap-2">
                        <User className="h-3.5 w-3.5 text-muted-foreground mt-0.5" />
                        <div>{a.voornaam} {a.achternaam}</div>
                      </div>
                      <div className="flex items-start gap-2">
                        <Mail className="h-3.5 w-3.5 text-muted-foreground mt-0.5" />
                        <a href={`mailto:${a.email}`} className="hover:underline break-all">{a.email}</a>
                      </div>
                      {a.telefoon && (
                        <div className="flex items-start gap-2">
                          <Phone className="h-3.5 w-3.5 text-muted-foreground mt-0.5" />
                          <a href={`tel:${a.telefoon}`} className="hover:underline">{a.telefoon}</a>
                        </div>
                      )}
                    </div>

                    {/* Bedrijfsinfo */}
                    <div className="border border-border rounded-lg p-3 bg-secondary/20 space-y-2">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">Bedrijf</div>
                      <div className="flex items-start gap-2">
                        <Building2 className="h-3.5 w-3.5 text-muted-foreground mt-0.5" />
                        <div className="font-medium">{a.bedrijfsnaam}</div>
                      </div>
                      <div className="flex items-start gap-2">
                        <FileText className="h-3.5 w-3.5 text-muted-foreground mt-0.5" />
                        <div><span className="text-muted-foreground text-xs">KvK:</span> {a.kvk || "—"}</div>
                      </div>
                      <div className="flex items-start gap-2">
                        <Calendar className="h-3.5 w-3.5 text-muted-foreground mt-0.5" />
                        <div className="text-xs text-muted-foreground">
                          Aangemeld: {a.created_at ? new Date(a.created_at).toLocaleString("nl-NL", { dateStyle: "medium", timeStyle: "short" }) : "—"}
                        </div>
                      </div>
                    </div>

                    {/* Bezorgadres */}
                    <div className="border border-border rounded-lg p-3 bg-secondary/20 space-y-1.5">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium flex items-center gap-1.5">
                        <MapPin className="h-3 w-3" /> Bezorgadres
                      </div>
                      {a.bezorgadres ? (
                        <div className="text-sm leading-tight">
                          <div>{a.bezorgadres.straat}</div>
                          <div>{a.bezorgadres.postcode} {a.bezorgadres.plaats}</div>
                          <div className="text-muted-foreground text-xs">{a.bezorgadres.land}</div>
                        </div>
                      ) : (
                        <div className="text-xs text-muted-foreground italic">Niet opgegeven</div>
                      )}
                    </div>

                    {/* Factuuradres */}
                    <div className="border border-border rounded-lg p-3 bg-secondary/20 space-y-1.5">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium flex items-center gap-1.5">
                        <MapPin className="h-3 w-3" /> Factuuradres
                        {a.factuuradres_gelijk && <Badge variant="outline" className="text-[9px] py-0 px-1.5">Gelijk aan bezorg</Badge>}
                      </div>
                      {a.factuuradres ? (
                        <div className="text-sm leading-tight">
                          <div>{a.factuuradres.straat}</div>
                          <div>{a.factuuradres.postcode} {a.factuuradres.plaats}</div>
                          <div className="text-muted-foreground text-xs">{a.factuuradres.land}</div>
                        </div>
                      ) : (
                        <div className="text-xs text-muted-foreground italic">Niet opgegeven</div>
                      )}
                    </div>
                  </div>

                  {/* Beoordelings-metadata (approved/rejected) */}
                  {(a.status === "approved" || a.status === "rejected") && (
                    <div className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground flex items-center gap-3 flex-wrap">
                      {a.goedgekeurd_op && <span>Goedgekeurd op {new Date(a.goedgekeurd_op).toLocaleString("nl-NL")}</span>}
                      {a.afgewezen_op && <span>Afgewezen op {new Date(a.afgewezen_op).toLocaleString("nl-NL")}</span>}
                      {a.goedgekeurd_door && <span>· door {a.goedgekeurd_door}</span>}
                      {a.afgewezen_door && <span>· door {a.afgewezen_door}</span>}
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </div>

      <Dialog open={!!actief} onOpenChange={(o) => !o && setActief(null)}>
        <DialogContent data-testid="goedkeur-dialog">
          <DialogHeader><DialogTitle>Aanvraag goedkeuren</DialogTitle></DialogHeader>
          {actief && (
            <div className="space-y-4 text-sm">
              <div className="bg-secondary/40 rounded-lg p-3">
                <div className="font-medium">{actief.bedrijfsnaam}</div>
                <div className="text-muted-foreground">{actief.voornaam} {actief.achternaam} · {actief.email}</div>
              </div>
              {(dupLoading || duplicaten.length > 0) && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="mogelijke-duplicaten">
                  <div className="font-medium mb-1 flex items-center gap-1.5 text-amber-800">
                    <Building2 className="h-4 w-4" /> Mogelijk al bestaande klant
                  </div>
                  {dupLoading ? (
                    <div className="text-xs text-amber-700 flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Zoeken naar bestaande klanten…</div>
                  ) : (
                    <>
                      <p className="text-xs text-amber-700 mb-2">Op basis van KvK of adres gevonden. Koppel om een dubbele klantkaart te voorkomen.</p>
                      <div className="space-y-1.5">
                        {duplicaten.map((o) => (
                          <div key={o.id} className="flex items-center justify-between gap-2 bg-white rounded-md border border-amber-200 px-2.5 py-1.5" data-testid={`duplicaat-${o.id}`}>
                            <div className="min-w-0">
                              <div className="text-sm font-medium truncate">{o.naam}</div>
                              <div className="text-[11px] text-muted-foreground truncate">
                                {[o.klantcode, o.plaats, o.kvk && `KvK ${o.kvk}`].filter(Boolean).join(" · ")}
                                <Badge variant="outline" className="ml-1.5 text-[9px] py-0 px-1 border-amber-300 text-amber-700">{o.match}</Badge>
                              </div>
                            </div>
                            <Button size="sm" variant={gekozenOrg?.id === o.id ? "default" : "outline"} className="shrink-0 h-7"
                              onClick={() => { setGekozenOrg(o); setOrgResults([]); setOrgQ(""); }} data-testid={`duplicaat-koppel-${o.id}`}>
                              {gekozenOrg?.id === o.id ? <><Check className="h-3.5 w-3.5 mr-1" />Gekoppeld</> : "Koppelen"}
                            </Button>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
              <div>
                <div className="font-medium mb-1.5 flex items-center gap-1.5"><Building2 className="h-4 w-4" /> Koppelen aan organisatie</div>
                <p className="text-xs text-muted-foreground mb-2">Laat leeg om automatisch een nieuwe prospect-organisatie aan te maken, of koppel aan een bestaande.</p>
                <Input value={orgQ} onChange={(e) => zoekOrg(e.target.value)}
                  placeholder="Zoek bestaande organisatie…" data-testid="goedkeur-org-zoek" autoComplete="off" />
                {gekozenOrg && (
                  <div className="mt-1.5 text-xs flex items-center gap-1.5 text-emerald-700">
                    <Check className="h-3.5 w-3.5" /> Gekoppeld aan: <strong>{gekozenOrg.naam}</strong>
                    <button onClick={() => { setGekozenOrg(null); setOrgQ(""); }} className="text-muted-foreground"><X className="h-3 w-3" /></button>
                  </div>
                )}
                {orgResults.length > 0 && !gekozenOrg && (
                  <div className="mt-1 border border-border rounded-lg max-h-40 overflow-y-auto">
                    {orgResults.map((o) => (
                      <button key={o.id} onClick={() => { setGekozenOrg(o); setOrgResults([]); }}
                        className="w-full text-left px-3 py-2 hover:bg-secondary text-sm" data-testid={`org-optie-${o.id}`}>
                        {o.naam} <span className="text-muted-foreground text-xs">{o.plaats}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setActief(null)}>Annuleren</Button>
            <Button onClick={bevestigGoedkeuren} disabled={busy} data-testid="goedkeur-bevestig">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : (gekozenOrg ? <Check className="h-4 w-4 mr-1" /> : <UserPlus className="h-4 w-4 mr-1" />)}
              {gekozenOrg ? "Goedkeuren + koppelen" : "Goedkeuren + nieuwe organisatie"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
